/**
 * Utility Functions
 */

import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import i18n from '../i18n/config';
import {
  formatDateTime as dayjsFormatDateTime,
  formatDate as dayjsFormatDate,
  formatTime as dayjsFormatTime,
  formatChartTime as dayjsFormatChartTime,
  formatRelativeTime as dayjsFormatRelativeTime,
  formatRelativeOrDate as dayjsFormatRelativeOrDate,
} from './dayjs';

/**
 * Merge class names with Tailwind CSS support
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Format a number with commas
 */
export function formatNumber(num: number): string {
  return new Intl.NumberFormat(i18n.language).format(num);
}

/**
 * Format bytes to human-readable size
 */
export function formatBytes(bytes: number, decimals: number = 2): string {
  if (bytes === 0) return i18n.t('common.zeroBytes');

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

/**
 * Format milliseconds to human-readable duration
 */
export function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  if (ms < 3600000) return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
  return `${Math.floor(ms / 3600000)}h ${Math.floor((ms % 3600000) / 60000)}m`;
}

/**
 * Format latency in milliseconds to seconds
 * Always displays in seconds with 3 decimal places
 */
export function formatLatency(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return 'N/A';
  return `${(ms / 1000).toFixed(3)}s`;
}

/**
 * Format a date/time string
 * Uses day.js for locale-aware formatting
 */
export function formatDateTime(date: string | Date): string {
  return dayjsFormatDateTime(date);
}

/**
 * Format date only (without time)
 */
export function formatDate(date: string | Date): string {
  return dayjsFormatDate(date);
}

/**
 * Format time only (without date)
 */
export function formatTime(date: string | Date): string {
  return dayjsFormatTime(date);
}

/**
 * Format time for charts (short format like "15:30")
 */
export function formatChartTime(date: string | Date): string {
  return dayjsFormatChartTime(date);
}

/**
 * Format relative time (e.g., "2 hours ago", "2小时前")
 * Uses day.js with locale-aware formatting
 */
export function formatRelativeTime(date: string | Date): string {
  return dayjsFormatRelativeTime(date);
}

/**
 * Format relative time with threshold
 * If date is older than threshold days, return formatted date instead
 */
export function formatRelativeOrDate(date: string | Date, thresholdDays = 7): string {
  return dayjsFormatRelativeOrDate(date, thresholdDays);
}

/**
 * Truncate a string with ellipsis
 */
export function truncate(str: string, length: number): string {
  if (str.length <= length) return str;
  return str.slice(0, length) + '...';
}

/**
 * Copy text to clipboard
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * Debounce a function
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      func(...args);
    }, wait);
  };
}

/**
 * Get status color classes
 */
export function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    success: 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800',
    blocked_input: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800',
    blocked_output: 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-800',
    upstream_error: 'bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800',
    timeout: 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700',
    active: 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800',
    revoked: 'bg-gray-100 text-gray-500 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700',
    // New statuses
    inactive: 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700',
    healthy: 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800',
    degraded: 'bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800',
    unhealthy: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800',
    admin: 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800',
    client: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800',
    production: 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800',
    development: 'bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800',
  };

  return colors[status] || 'bg-gray-100 text-gray-700 border-gray-200';
}

/**
 * Get status display label
 */
export function getStatusLabel(status: string): string {
  // Try to find translation in specific namespaces
  if (['success', 'blocked_input', 'blocked_output', 'upstream_error', 'timeout'].includes(status)) {
    return i18n.t(`logs.status.${status}`);
  }
  
  if (status === 'active') return i18n.t('common.active');
  if (status === 'inactive') return i18n.t('common.inactive');
  if (status === 'revoked') return i18n.t('common.revoked');

  return status;
}

